import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { UpgradeModal } from "../components/UpgradeModal";
import { useAuth } from "./useAuth";
import { billingCheckout } from "../lib/api";

const ProContext = createContext(null);

export const ProProvider = ({ children }) => {
  const { user, requireAuth } = useAuth();
  const [modalFeature, setModalFeature] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  const isPro = !!user?.is_pro;

  // Open the upgrade modal. Signed-out users are prompted to create an account
  // first; the subscription modal then appears after sign-in.
  const openUpgrade = useCallback(
    (feature = "default") => {
      if (!user) {
        requireAuth(() => {});
        return;
      }
      setModalFeature(feature);
    },
    [user, requireAuth]
  );

  // Gate a premium action: signed-in Pro users run it; otherwise prompt
  // account creation (signed-out) or upgrade (signed-in free).
  const attempt = useCallback(
    (feature, fn) => {
      if (isPro) {
        fn && fn();
        return;
      }
      if (!user) {
        requireAuth(() => {});
        return;
      }
      setModalFeature(feature);
    },
    [isPro, user, requireAuth]
  );

  // After a gated sign-in, surface the subscription/welcome modal.
  useEffect(() => {
    if (user && !isPro) {
      const intent = sessionStorage.getItem("lbs_after_login");
      if (intent === "upgrade") {
        sessionStorage.removeItem("lbs_after_login");
        setModalFeature("welcome");
      }
    }
  }, [user, isPro]);

  const checkout = useCallback(async (plan) => {
    try {
      setCheckingOut(true);
      // Remember where the founder was so we can return them there after paying.
      sessionStorage.setItem("lbs_return_after_pro", window.location.pathname + window.location.search);
      const { url } = await billingCheckout(plan);
      window.location.href = url;
      // Fallback: if navigation is blocked, reset so the button isn't stuck.
      setTimeout(() => setCheckingOut(false), 6000);
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setCheckingOut(false);
    }
  }, []);

  return (
    <ProContext.Provider value={{ isPro, attempt, openUpgrade, checkout, checkingOut }}>
      {children}
      <UpgradeModal
        feature={modalFeature}
        checkingOut={checkingOut}
        onClose={() => setModalFeature(null)}
        onCheckout={checkout}
      />
    </ProContext.Provider>
  );
};

export const usePro = () => useContext(ProContext);
